import {
  BUILDER_MAP_MODE,
  BUILDER_MAP_COLOR_INACTIVE,
  BUILDER_MAP_COLOR_POSTCODE,
} from './const'
import {
  isPointInPolygon,
  getDistanceToPolygonBorder,
} from '../../shared/libraries/geo'
import { getAreaFromLatLngBound } from '../../Components/DrawMap/util'

/**
 * Processes raw GEOJSON MultiPolygon path and returns formatted path that can be consumed by GMap
 * @param {array} coords Raw GEOJSON MultiPolygon path
 * @returns {array} Formatted path
 */
export const getMultiPolygonPath = coords => {
  if (Array.isArray(coords[0])) {
    return coords.map(getMultiPolygonPath)
  }
  return { lng: coords[0], lat: coords[1] }
}

/**
 * Figures out method based on isMultiPolygon arg and
 * then processes raw GEOJSON path and returns formatted path that can be consumed by GMap
 * @param {array} args.coords Raw GEOJSON MultiPolygon path
 * @param {boolean} args.isMultiPolygon Indicates whether path is a MultiPolygon
 * @returns {array} Formatted path
 */
export const getOverlayPath = ({ coords, isMultiPolygon }) => {
  if (!coords) return []
  if (isMultiPolygon) {
    return getMultiPolygonPath(coords.map(([array]) => array))
  }

  return [
    coords[0].map(([lng, lat]) => ({
      lat,
      lng,
    })),
  ]
}

/**
 * Returns overlay appereance props based on received args
 * @param {boolean} args.isEdit Is map in a EDIT mode
 * @param {boolean} args.isPostcode Is current overlay represents a postcode area
 * @returns {object} Appereance settings
 */
export const getOverlayColorAndOpacity = ({ isEdit, isPostcode, type }) => {
  if (type == 'subPolygon') {
    return {
      color: 'green',
      fillOpacity: 0,
      strokeOpacity: 1,
    }
  }
  if (isPostcode) {
    return {
      color: BUILDER_MAP_COLOR_POSTCODE,
      fillOpacity: 0,
      strokeOpacity: 1,
    }
  }
  if (isEdit) {
    return {
      color: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
      fillOpacity: 0.3,
      strokeOpacity: 1,
    }
  }
  return {
    color: BUILDER_MAP_COLOR_INACTIVE,
    fillOpacity: 0.2,
    strokeOpacity: 0.4,
  }
}

/**
 * Small utility to calculate zIndex of the polygon
 * @param {boolean} param.isPostcode
 * @param {boolean} param.isIRIS
 * @returns {number} zIndex value
 */
const getZIndex = ({ isPostcode, isIRIS }) => {
  if (isPostcode) return 0
  if (isIRIS) return 5
  return 10
}

/**
 * Formats GEOJSON features into the initial overlays that DrawMap can consume
 * @param {array} rawFeatures GEOJSON
 * @returns {array} Formatted data that can be consumed by the DrawMap
 */
export const formatFeaturesToOverlays = (rawFeatures, options = {}) => {
  const { isPostcode = false, isIRIS } = options
  const mode = isIRIS ? BUILDER_MAP_MODE.TOGGLE : options.mode
  const isEdit = mode === BUILDER_MAP_MODE.EDIT

  return rawFeatures.map(feature => {
    const { id, name, customToggled = false, type } = feature.properties

    const { color, fillOpacity, strokeOpacity } = getOverlayColorAndOpacity({
      isEdit,
      isPostcode,
      type,
    })
    if (!feature.geometry) return {}

    const paths = getOverlayPath({
      coords: feature.geometry.coordinates,
      isMultiPolygon: feature.geometry.type == 'MultiPolygon',
    })

    return {
      id,
      paths,
      color,
      name,
      metadata: feature.properties,
      mapPolygonProps: {
        key: id,
        editingBlocked: !isEdit || isPostcode,
        canBeDeleted: isEdit,
        fillOpacity,
        strokeOpacity,
        customToggled,
        payload: {
          label: name,
          type,
        },
        zIndex: getZIndex({ isPostcode, isIRIS }),
      },
      isPostcode,
    }
  })
}

/**
 * Formats GMap overlay to GEOJSON
 * @param {string} args.countryCode Country code
 * @param {array} args.boundary Overlay's path
 */
export const getFeatureFromOverlay = ({ countryCode, boundary }) => {
  return {
    countryCode: countryCode,
    layer: 'MissionBoundaries',
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          ...boundary.map(({ longitude, latitude }) => [longitude, latitude]),
          [boundary[0].longitude, boundary[0].latitude],
        ],
      ],
    },
  }
}
/**
 * Checked is polygons do not overlaps each other
 * @param {array} renderedOverlays existing polygons
 * @param {array} boundary current processed polygon
 * @returns {boolean} is polygons overlaps each other if distance to polyogn  border > 50m
 */
export const checkIfOverlaysIntersect = (
  boundary,
  renderedOverlays,
  featureId,
) => {
  let isIntersects = false
  const poly = boundary.map(p => [p.longitude, p.latitude])

  boundary.forEach(point =>
    renderedOverlays.forEach(polygon => {
      if (polygon.id == featureId) return
      const isInPolygon = isPointInPolygon(
        [point.longitude, point.latitude],
        polygon.paths[0].map(({ lat, lng }) => [lng, lat]),
      )

      if (isInPolygon) {
        const distance = getDistanceToPolygonBorder(
          [point.longitude, point.latitude],
          polygon.paths[0].map(({ lat, lng }) => [lng, lat]),
        )
        if (distance > 50) {
          isIntersects = true
        }
      }
    }),
  )

  renderedOverlays.forEach(polygon => {
    if (polygon.id == featureId) return
    polygon.paths[0].forEach(point => {
      const isInPolygon = isPointInPolygon([point.lng, point.lat], poly)
      if (isInPolygon) {
        const distance = getDistanceToPolygonBorder(
          [point.lng, point.lat],
          poly,
        )
        if (distance > 50) {
          isIntersects = true
        }
      }
    })
  })

  return isIntersects
}

export const getFormatedArea = async latLngBound => {
  const rawArea = getAreaFromLatLngBound(latLngBound)
  const formattedArea = {
    type: 'Polygon',
    coordinates: [
      [
        ...rawArea.map(({ longitude, latitude }) => [longitude, latitude]),
        [rawArea[0].longitude, rawArea[0].latitude],
      ],
    ],
  }
  return formattedArea
}

export const isPostcode = input => {
  return /^[0-9]+/.test(input)
}

export const filterPostcodes = ({ postcodes, countryCode }) =>
  postcodes.reduce(
    (acc, postcode) => {
      if (postcode.name) {
        if (countryCode === 'NZ' || countryCode === 'AU') {
          if (isPostcode(postcode.name)) {
            acc.oldP[postcode.id] = postcode
          } else {
            acc.newP[postcode.id] = postcode
          }
        } else {
          acc.oldP[postcode.id] = postcode
        }
      } else {
        acc.invalidP[postcode.id] = postcode
      }
      return acc
    },
    { oldP: {}, newP: {}, invalidP: {} },
  )
